import { API_BASE, getCsrfToken } from '../../config';
import React, { useState, useEffect } from 'react';
import LeaveCalendar from './LeaveCalendar';
import { Home, Calendar, FileText, CheckCircle, Clock, PanelRightOpen, X } from 'lucide-react';
import { createLeaveRequestViaSupabase } from '../../utils/supabaseClient';
import { useTranslation } from '../../i18n/LanguageContext';
import LanguageSelector from '../LanguageSelector';

const LeaveManagement = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [leavesDrawerOpen, setLeavesDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('new-request');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [assignedToMe, setAssignedToMe] = useState([]);
  const [myBalances, setMyBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: '',
    assignedTo: '',      // Niveau 1 - Obligatoire
    validator2: '',      // Niveau 2 - Optionnel
    validator3: '',      // Niveau 3 - Optionnel
    emergencyContact: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const check = () => {
      setIsMobile(mql.matches);
      if (!mql.matches) setLeavesDrawerOpen(false);
    };
    check();
    mql.addEventListener('change', check);
    return () => mql.removeEventListener('change', check);
  }, []);

  useEffect(() => {
    if (activeTab === 'my-requests') {
      loadMyRequests();
    } else if (activeTab === 'assigned-to-me') {
      loadAssignedToMe();
    } else if (activeTab === 'my-balances') {
      loadMyBalances();
    }
  }, [activeTab]);

  // Real-time: refetch "Assigned to me" often so when someone else approves/rejects, status and badge update
  useEffect(() => {
    if (activeTab !== 'assigned-to-me') return;
    const interval = setInterval(loadAssignedToMe, 8000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Refetch when user comes back to this tab (visibility) so status is up to date
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && activeTab === 'assigned-to-me') {
        loadAssignedToMe();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [activeTab]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const loadInitialData = async () => {
    try {
      const [typesResponse, usersResponse] = await Promise.all([
        fetch(`${API_BASE}/api/leaves/types`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/api/leaves/assignable-users`, { headers: getAuthHeaders() })
      ]);

      const typesData = await typesResponse.json();
      const usersData = await usersResponse.json();

      setLeaveTypes(typesData.leaveTypes || []);
      setAssignableUsers(usersData.users || []);
    } catch (error) {
      console.error('Erreur chargement données initiales:', error);
      setMessage(`❌ ${t('leaves.error_loading_data')}`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const loadMyRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/leaves/my-requests`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setMyRequests(data.requests || []);
    } catch (error) {
      console.error('Erreur chargement mes demandes:', error);
    }
  };

  const loadAssignedToMe = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/leaves/assigned-to-me`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      console.log('📥 Demandes assignées reçues:', data);
      setAssignedToMe(data.requests || []);
    } catch (error) {
      console.error('Erreur chargement demandes assignées:', error);
    }
  };

  const loadMyBalances = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/leaves/my-balances`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setMyBalances(data.balances || []);
    } catch (error) {
      console.error('Erreur chargement soldes:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateWorkingDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    let workingDays = 0;
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) workingDays++;
    }
    
    return workingDays;
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    const level1 = String(formData.assignedTo || '').trim();
    if (!formData.leaveTypeId || !formData.startDate || !formData.endDate || !level1) {
      setMessage(`❌ ${t('leaves.fill_required_fields')} (${t('leaves.level_1_manager')})`);
      setTimeout(() => setMessage(''), 4000);
      return;
    }

    setLoading(true);
      setMessage(`🔄 ${t('leaves.sending')}`);

    try {
      // Préparer les validateurs selon les niveaux sélectionnés (chaque niveau peut être une personne différente)
      const v2 = String(formData.validator2 || '').trim();
      const v3 = String(formData.validator3 || '').trim();
      const validators = {
        level1: level1,
        level2: v2 || null,
        level3: v3 || null
      };

      const requestBody = {
        leaveTypeId: formData.leaveTypeId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason,
        emergencyContact: formData.emergencyContact,
        validators: validators
      };

      console.log('📤 Envoi demande congé:', requestBody);
      
      // 🚀 SOLUTION DE CONTOURNEMENT : Essayer Supabase en premier, puis Render en fallback
      const token = localStorage.getItem('token');
      console.log('🔑 Token présent:', token ? 'OUI' : 'NON');
      let data;
      
      try {
        // Essayer Supabase d'abord
        console.log('🔄🔄🔄 TENTATIVE VIA SUPABASE...');
        console.log('🔄 createLeaveRequestViaSupabase importé:', typeof createLeaveRequestViaSupabase);
        data = await createLeaveRequestViaSupabase(
          {
            ...requestBody,
            assignedTo: requestBody.validators.level1,
            validator2: requestBody.validators.level2,
            validator3: requestBody.validators.level3
          },
          token
        );
        
        if (data.success) {
          console.log('✅ Succès via Supabase:', data);
          setMessage(`✅ ${data.message || t('leaves.leave_request_created_success')}`);
          setFormData({
            leaveTypeId: '',
            startDate: null,
            endDate: null,
            reason: '',
            emergencyContact: '',
            assignedTo: '',
            validator2: '',
            validator3: '',
            halfDay: false,
            halfDayPeriod: '',
            requestPriority: 'normal',
            medicalCertificatePath: '',
            justificationDocumentPath: '',
            legalJustification: '',
            administrationApprovalCode: ''
          });
          setTimeout(() => setMessage(''), 5000);
          // Notify only the selected validator(s) — leave was created via Supabase
          const validatorIds = [
            requestBody.validators?.level1,
            requestBody.validators?.level2,
            requestBody.validators?.level3
          ].filter(Boolean);
          (async () => {
            const csrf = await getCsrfToken();
            if (!csrf) return;
            fetch(`${API_BASE}/api/leaves/notify-new-request`, {
              method: 'POST',
              headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
              body: JSON.stringify(
                validatorIds.length > 0
                  ? { validatorIds }
                  : requestBody.validators?.level1
                    ? { assignedTo: requestBody.validators.level1 }
                    : {}
              )
            }).then(r => r.json()).then(() => {}).catch(() => {});
          })();
          return;
        } else {
          throw new Error(data.error || t('leaves.supabase_error'));
        }
      } catch (supabaseError) {
        console.warn('⚠️ Erreur Supabase, fallback vers Render:', supabaseError);
        
        // Fallback vers Render (GET)
        const params = new URLSearchParams({
          leaveTypeId: requestBody.leaveTypeId,
          startDate: requestBody.startDate,
          endDate: requestBody.endDate,
          reason: requestBody.reason || '',
          emergencyContact: requestBody.emergencyContact || '',
          assignedTo: requestBody.validators.level1 || '',
          validator2: requestBody.validators.level2 || '',
          validator3: requestBody.validators.level3 || ''
        });
        
        const fullUrl = `${API_BASE}/api/leaves/requests/create?${params.toString()}`;
        console.log('🌐 Fallback Render - URL GET:', fullUrl);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.error('⏱️ TIMEOUT: La requête a pris plus de 30 secondes');
          controller.abort();
        }, 30000);

        let response;
        try {
          response = await fetch(fullUrl, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (fetchError) {
          clearTimeout(timeoutId);
          if (fetchError.name === 'AbortError') {
            throw new Error(t('leaves.request_timeout'));
          }
          throw fetchError;
        }

        if (response.status === 202 || response.ok) {
          data = await response.json();
          if (data.success || data.processing) {
            setMessage(`✅ ${data.message || t('leaves.leave_request_received_processing')}`);
            setFormData({
              leaveTypeId: '',
              startDate: null,
              endDate: null,
              reason: '',
              emergencyContact: '',
              assignedTo: '',
              validator2: '',
              validator3: '',
              halfDay: false,
              halfDayPeriod: '',
              requestPriority: 'normal',
              medicalCertificatePath: '',
              justificationDocumentPath: '',
              legalJustification: '',
              administrationApprovalCode: ''
            });
            setTimeout(() => setMessage(''), 5000);
            return;
          } else {
            throw new Error(data.error || t('leaves.unknown_error'));
          }
        } else {
          const errorData = await response.json().catch(() => ({ error: t('leaves.unknown_error') }));
          throw new Error(errorData.error || t('leaves.http_error', { status: response.status }) || `Erreur ${response.status}`);
        }
      }

    } catch (error) {
      console.error('Erreur création demande:', error);
      const errMsg = (error && (error.message || error.error)) || t('leaves.error_creating_request');
      setMessage(`❌ ${errMsg}`);
      setTimeout(() => setMessage(''), 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleValidation = async (requestId, decision, comments = '') => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        setMessage('❌ ' + (t('leaves.error_validation') || 'Please refresh the page and try again.'));
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/requests/${requestId}/validation`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify({ decision, comments })
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`✅ ${data.message}`);
        await loadAssignedToMe();
        setTimeout(() => setMessage(''), 5000);
      } else {
        setMessage(`❌ ${data.error}`);
        setTimeout(() => setMessage(''), 5000);
      }

    } catch (error) {
      console.error('Erreur validation:', error);
      setMessage(`❌ ${t('leaves.error_validation')}`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const cancelRequest = async (requestId) => {
    if (!window.confirm(t('leaves.cancel_request_confirm'))) return;

    try {
      const csrf = await getCsrfToken();
      if (!csrf) {
        setMessage('❌ ' + (t('leaves.error_cancellation') || 'Please refresh the page and try again.'));
        return;
      }
      const response = await fetch(`${API_BASE}/api/leaves/requests/${requestId}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf }
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`✅ ${t('leaves.request_cancelled_success')}`);
        loadMyRequests();
        setTimeout(() => setMessage(''), 5000);
      } else {
        setMessage(`❌ ${data.error}`);
        setTimeout(() => setMessage(''), 5000);
      }

    } catch (error) {
      console.error('Erreur annulation demande:', error);
      setMessage(`❌ ${t('leaves.error_cancellation')}`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    // Utiliser la locale du navigateur ou fr-FR par défaut
    const locale = navigator.language || 'fr-FR';
    return new Date(dateString).toLocaleDateString(locale);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f39c12';
      case 'approved': return '#2ecc71';
      case 'rejected': return '#e74c3c';
      case 'cancelled': return '#95a5a6';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t('leaves.pending');
      case 'approved': return t('leaves.approved');
      case 'rejected': return t('leaves.rejected');
      case 'cancelled': return t('leaves.cancelled');
      default: return status;
    }
  };

  const renderWorkflowProgress = (request) => {
    if (!request.requires_multi_validation) {
      return (
        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
          🔄 {t('leaves.simple_validation')}
        </div>
      );
    }

    let validations = [];
    try {
      // Sécuriser le parsing JSON
      if (typeof request.all_validations === 'string') {
        validations = JSON.parse(request.all_validations);
      } else if (Array.isArray(request.all_validations)) {
        validations = request.all_validations;
      }
    } catch (error) {
      console.error('❌ Erreur parsing validations:', error);
      console.log('📄 Données reçues:', request.all_validations);
      validations = [];
    }
    
    return (
      <div style={{ marginTop: '10px' }}>
        <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>
                          🔄 {t('leaves.workflow_multi_validation', { current: request.current_step, total: request.total_validation_steps })}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {validations.map((validation, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                background: validation.status === 'approved' ? 'rgba(46, 204, 113, 0.2)' :
                           validation.status === 'rejected' ? 'rgba(231, 76, 60, 0.2)' :
                           'rgba(243, 156, 18, 0.2)',
                border: `1px solid ${validation.status === 'approved' ? '#2ecc71' :
                                   validation.status === 'rejected' ? '#e74c3c' : '#f39c12'}`,
                color: '#2c3e50'
              }}
            >
              <span>{validation.step === 1 ? '👨‍💼' : validation.step === 2 ? '🏢' : '⭐'}</span>
              <span>{validation.validator_name}</span>
              <span>
                {validation.status === 'approved' ? '✅' :
                 validation.status === 'rejected' ? '❌' : '⏳'}
              </span>
              {!validation.is_required && <span style={{ opacity: 0.7 }}>{t('leaves.optional')}</span>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Badge: only count requests where it's MY turn to act (not "wait your turn")
  const pendingAssignedCount = assignedToMe.filter(req => {
    if (req.status !== 'pending') return false;
    const step = Number(req.current_step);
    const myStep = Number(req.my_validation_step);
    const isMyTurn = !req.requires_multi_validation || (req.my_validation_step != null && req.current_step != null && step === myStep);
    return isMyTurn;
  }).length;
  const workingDays = calculateWorkingDays();

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header: mobile = compact bar + drawer; desktop = full title + subtitle */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '12px 16px' : '24px 20px 20px',
        background: isMobile ? 'rgba(10, 14, 39, 0.85)' : 'transparent',
        backdropFilter: isMobile ? 'blur(20px)' : 'none',
        borderBottom: isMobile ? '1px solid rgba(255,255,255,0.1)' : 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isMobile ? 0 : '6px',
          gap: '16px',
          minWidth: 0
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          {isMobile ? (
            <>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={22} style={{ flexShrink: 0 }} />
                {t('leaves.title')}
              </div>
              <button
                type="button"
                onClick={() => setLeavesDrawerOpen(true)}
                aria-label={t('home.open_menu') || 'Menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <PanelRightOpen size={20} />
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 700, maxWidth: '100%', letterSpacing: '-0.5px', lineHeight: 1.2, flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Calendar size={32} />
                {t('leaves.title')}
              </div>
            </>
          )}
        </div>
        {!isMobile && (
          <p style={{ fontSize: 'clamp(14px, 2vw, 16px)', color: 'rgba(255, 255, 255, 0.7)', margin: '8px 0 0 0', letterSpacing: '0.2px', fontWeight: 500 }}>
            {t('leaves.description')}
          </p>
        )}
      </div>

      {/* Mobile drawer */}
      {isMobile && leavesDrawerOpen && (
        <div role="presentation" onClick={() => setLeavesDrawerOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ width: 'min(320px, 88vw)', height: '100%', background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)', padding: '20px 16px', boxSizing: 'border-box', overflowY: 'auto', borderLeft: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{t('leaves.title')}</span>
              <button type="button" onClick={() => setLeavesDrawerOpen(false)} aria-label={t('common.close') || 'Close'} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>{t('leaves.description')}</p>
            <div>
              <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10 }} />
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="leaves-content" style={{
        paddingTop: isMobile ? '88px' : '140px',
        paddingBottom: isMobile ? '24px' : '40px',
        paddingLeft: isMobile ? '12px' : '20px',
        paddingRight: isMobile ? '12px' : '20px',
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'visible'
      }}>

        {message && (
          <div style={{
            background: message.includes('✅') 
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))',
            border: `1px solid ${message.includes('✅') ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            color: message.includes('✅') ? '#10b981' : '#ef4444',
            padding: '16px 20px',
            borderRadius: '16px',
            marginBottom: '24px',
            textAlign: 'center',
            fontWeight: 600,
            backdropFilter: 'blur(10px)',
            fontSize: '15px'
          }}>
            {message}
          </div>
        )}

        {/* Navigation Tabs — on mobile: horizontal scroll so all 5 tabs visible */}
        <div style={{
          display: 'flex',
          gap: isMobile ? '8px' : '12px',
          marginBottom: isMobile ? '16px' : '24px',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingBottom: isMobile ? '12px' : '8px',
          paddingRight: isMobile ? '16px' : 0,
          WebkitOverflowScrolling: 'touch',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%'
        }}>
          <button
            onClick={() => setActiveTab('new-request')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 24px',
              borderRadius: '12px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'new-request'
                ? 'linear-gradient(135deg, #00e676, #00b85c)'
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexShrink: 0,
              boxShadow: activeTab === 'new-request' ? '0 4px 16px rgba(0, 230, 118, 0.3)' : 'none'
            }}
          >
            <FileText size={18} />
            {t('leaves.request_leave')}
          </button>

          <button
            onClick={() => setActiveTab('my-requests')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 24px',
              borderRadius: '12px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'my-requests'
                ? 'linear-gradient(135deg, #00e676, #00b85c)'
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexShrink: 0,
              boxShadow: activeTab === 'my-requests' ? '0 4px 16px rgba(0, 230, 118, 0.3)' : 'none'
            }}
          >
            <FileText size={18} />
            {t('leaves.my_requests')}
          </button>

          <button
            onClick={() => setActiveTab('my-balances')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 24px',
              borderRadius: '12px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'my-balances'
                ? 'linear-gradient(135deg, #00e676, #00b85c)'
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexShrink: 0,
              boxShadow: activeTab === 'my-balances' ? '0 4px 16px rgba(0, 230, 118, 0.3)' : 'none'
            }}
          >
            <Clock size={18} />
            {t('leaves.my_balances')}
          </button>

          <button
            onClick={() => setActiveTab('calendar')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 24px',
              borderRadius: '12px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'calendar'
                ? 'linear-gradient(135deg, #00e676, #00b85c)'
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flexShrink: 0,
              boxShadow: activeTab === 'calendar' ? '0 4px 16px rgba(0, 230, 118, 0.3)' : 'none'
            }}
          >
            <Calendar size={18} />
            {t('navigation.calendar')}
          </button>

          <button
            onClick={() => setActiveTab('assigned-to-me')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 24px',
              ...(pendingAssignedCount > 0 && { paddingLeft: isMobile ? '20px' : '28px', paddingRight: isMobile ? '20px' : '28px' }),
              borderRadius: '12px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'assigned-to-me'
                ? 'linear-gradient(135deg, #00e676, #00b85c)'
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              position: 'relative',
              flexShrink: 0,
              minWidth: isMobile ? 140 : 160,
              lineHeight: 1,
              boxShadow: activeTab === 'assigned-to-me' ? '0 4px 16px rgba(0, 230, 118, 0.3)' : 'none'
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
              <CheckCircle size={18} />
            </span>
            <span style={{ display: 'inline-block', lineHeight: 1, whiteSpace: 'nowrap' }}>{t('leaves.assigned_to_me')}</span>
            {pendingAssignedCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                background: '#ef4444',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 700,
                lineHeight: 1,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>
                {pendingAssignedCount}
              </span>
            )}
          </button>
      </div>

      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '12px',
        padding: isMobile ? '16px' : '30px',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        color: '#2c3e50',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {activeTab === 'new-request' && (
          <div style={{ width: '100%', minWidth: 0 }}>
            <h3 style={{ color: '#2c3e50', marginBottom: isMobile ? '16px' : '25px', fontSize: isMobile ? '1.25rem' : '24px', fontWeight: 'bold' }}>
              📝 {t('leaves.submit_leave_request')}
            </h3>
            
            <div style={{
              background: 'rgba(52, 152, 219, 0.1)',
              border: '1px solid #3498db',
              padding: isMobile ? '12px' : '15px',
              borderRadius: '8px',
              marginBottom: isMobile ? '16px' : '20px'
            }}>
              <h4 style={{ color: '#2980b9', margin: '0 0 8px 0', fontSize: isMobile ? '13px' : '14px' }}>
                ℹ️ {t('leaves.validation_system_title')}
              </h4>
              <ul style={{ margin: 0, paddingLeft: isMobile ? '18px' : '20px', fontSize: isMobile ? '12px' : '13px', color: '#34495e' }}>
                <li><strong>{t('leaves.level_1')}:</strong> {t('leaves.level_1_desc')}</li>
                <li><strong>{t('leaves.level_2')}:</strong> {t('leaves.level_2_desc')}</li>
                <li><strong>{t('leaves.level_3')}:</strong> {t('leaves.level_3_desc')}</li>
              </ul>
            </div>
            
            <form onSubmit={handleSubmitRequest} style={{ width: '100%', minWidth: 0 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: isMobile ? '14px' : '20px',
                marginBottom: isMobile ? '18px' : '25px'
              }}>
                
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    {t('leaves.type')} *
                  </label>
                  <select
                    name="leaveTypeId"
                    value={formData.leaveTypeId}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #bdc3c7',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  >
                    <option value="">{t('leaves.select_type')}</option>
                    {leaveTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} {type.is_paid ? `(${t('leaves.paid')})` : `(${t('leaves.unpaid')})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    {t('leaves.start_date')} *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #bdc3c7',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    {t('leaves.end_date')} *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #bdc3c7',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: '#2c3e50'
                  }}>
                    {t('leaves.emergency_contact')}
                  </label>
                  <input
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleFormChange}
                    placeholder={t('leaves.emergency_contact_placeholder')}
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #bdc3c7',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  />
                </div>
              </div>

              {/* Affichage des jours ouvrés */}
              {workingDays > 0 && (
                <div style={{
                  background: 'rgba(46, 204, 113, 0.1)',
                  border: '1px solid #27ae60',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#27ae60', fontWeight: 'bold', marginBottom: '5px' }}>
                    📅 {workingDays > 1 ? t('leaves.working_days_plural', { count: workingDays }) : t('leaves.working_days_singular', { count: workingDays })}
                  </div>
                </div>
              )}

              {/* Section Validateurs - 3 niveaux */}
              <div style={{
                background: 'rgba(44, 62, 80, 0.1)',
                padding: isMobile ? '14px' : '20px',
                borderRadius: '8px',
                marginBottom: isMobile ? '18px' : '25px',
                border: '1px solid #bdc3c7',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h4 style={{ color: '#2c3e50', marginBottom: isMobile ? '12px' : '15px', fontSize: isMobile ? '0.95rem' : undefined }}>👥 {t('leaves.validators_by_level')}</h4>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: isMobile ? '12px' : '15px'
                }}>
                  
                  {/* Niveau 1 - Obligatoire */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#2c3e50'
                    }}>
                      👨‍💼 {t('leaves.level_1_manager')}
                    </label>
                  <select
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '2px solid #e74c3c',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  >
                      <option value="">{t('leaves.select_manager')}</option>
                      {assignableUsers.filter(user => ['manager', 'admin'].includes(user.role)).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.role})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Level 2 - Optional */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#2c3e50'
                    }}>
                      🏢 {t('leaves.level_2_hr')}
                    </label>
                    <select
                      name="validator2"
                      value={formData.validator2}
                      onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #f39c12',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  >
                    <option value="">{t('leaves.select_validator_2')}</option>
                      {assignableUsers.filter(user => ['manager', 'admin', 'hr'].includes(user.role)).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.role})
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#f39c12', fontSize: '11px' }}>
                      {t('leaves.additional_hr_validation')}
                    </small>
                  </div>

                  {/* Level 3 - Optional */}
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '500',
                      color: '#2c3e50'
                    }}>
                      ⭐ {t('leaves.level_3_direction')}
                    </label>
                    <select
                      name="validator3"
                      value={formData.validator3}
                      onChange={handleFormChange}
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px' : '12px',
                      borderRadius: '6px',
                      border: '1px solid #9b59b6',
                      fontSize: isMobile ? '16px' : '14px',
                      color: '#2c3e50',
                      boxSizing: 'border-box',
                      maxWidth: '100%'
                    }}
                  >
                    <option value="">{t('leaves.select_validator_3')}</option>
                      {assignableUsers.filter(user => ['manager', 'admin'].includes(user.role)).map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name} ({user.role})
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#9b59b6', fontSize: '11px' }}>
                      {t('leaves.final_management_validation')}
                    </small>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: isMobile ? '18px' : '25px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#2c3e50'
                }}>
                  {t('leaves.reason_comment')}
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleFormChange}
                  rows={isMobile ? 3 : 4}
                  placeholder={t('leaves.reason_placeholder')}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    borderRadius: '6px',
                    border: '1px solid #bdc3c7',
                    fontSize: isMobile ? '16px' : '14px',
                    resize: 'vertical',
                    color: '#2c3e50',
                    boxSizing: 'border-box',
                    maxWidth: '100%'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: isMobile ? '14px 20px' : '15px 30px',
                  width: isMobile ? '100%' : undefined,
                  background: loading ? '#95a5a6' : 'linear-gradient(135deg, #2ecc71, #27ae60)',
                  color: '#ecf0f1',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                {loading ? `⏳ ${t('leaves.sending')}` : `📤 ${t('leaves.submit_request')}`}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'my-requests' && (
          <div>
            <h3 style={{ color: '#2c3e50', marginBottom: '25px', fontSize: '24px', fontWeight: 'bold' }}>
              📋 {t('leaves.my_requests_title')}
            </h3>
            
            {myRequests.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: '#7f8c8d',
                padding: '40px'
              }}>
                <p>📝 {t('leaves.no_requests_empty')}</p>
                <button
                  onClick={() => setActiveTab('new-request')}
                  style={{
                    padding: '10px 20px',
                    background: '#3498db',
                    color: '#ecf0f1',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    marginTop: '15px'
                  }}
                >
                  {t('leaves.create_first_request')}
                </button>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px'
              }}>
                {myRequests.map(request => (
                  <div
                    key={request.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      padding: '20px',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${getStatusColor(request.status)}`,
                      marginBottom: '15px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '15px',
                      gap: '10px',
                      minWidth: 0
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h4 style={{
                          color: '#2c3e50',
                          margin: '0 0 5px 0'
                        }}>
                          {request.leave_type_name}
                        </h4>
                        <p style={{
                          color: '#7f8c8d',
                          margin: '0 0 5px 0',
                          fontSize: '14px'
                        }}>
                          {t('leaves.from')} {formatDate(request.start_date)} {t('leaves.to')} {formatDate(request.end_date)} ({request.days_requested} {t('leaves.days')})
                        </p>
                      </div>
                      
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: getStatusColor(request.status),
                          color: '#ecf0f1',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}>
                          {getStatusText(request.status)}
                        </span>
                        
                        {request.status === 'pending' && (
                          <button
                            onClick={() => cancelRequest(request.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#e74c3c',
                              color: '#ecf0f1',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginTop: '8px',
                              display: 'block',
                              marginLeft: 'auto'
                            }}
                          >
                            {t('common.cancel')}
                          </button>
                        )}
                      </div>
                    </div>

                    {renderWorkflowProgress(request)}
                    
                    {request.reason && (
                      <div style={{
                        background: 'rgba(149, 165, 166, 0.1)',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '10px',
                        border: '1px solid #bdc3c7'
                      }}>
                        <small style={{ color: '#7f8c8d', fontWeight: '500' }}>{t('leaves.reason')}:</small>
                        <p style={{ color: '#2c3e50', margin: '5px 0 0 0', fontSize: '14px' }}>
                          {request.reason}
                        </p>
                      </div>
                    )}
                    
                    {request.rejection_reason && (
                      <div style={{
                        background: 'rgba(231, 76, 60, 0.1)',
                        border: '1px solid #e74c3c',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '10px'
                      }}>
                        <small style={{ color: '#c0392b', fontWeight: '500' }}>{t('leaves.rejection_reason_label')}</small>
                        <p style={{ color: '#2c3e50', margin: '5px 0 0 0', fontSize: '14px' }}>
                          {request.rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'my-balances' && (
          <div>
            <h3 style={{ color: '#2c3e50', marginBottom: '25px', fontSize: '24px', fontWeight: 'bold' }}>
              💰 {t('leaves.my_leave_balances')}
            </h3>
            
            {myBalances.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: '#7f8c8d',
                padding: '40px',
                maxWidth: '480px',
                margin: '0 auto'
              }}>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>💼 {t('leaves.no_balance_configured')}</p>
                <p style={{ fontSize: '13px', marginBottom: '12px', lineHeight: 1.5 }}>{t('leaves.balance_flow_hint')}</p>
                <small style={{ color: '#95a5a6' }}>{t('leaves.contact_admin')}</small>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px'
              }}>
                {myBalances.map(balance => (
                  <div
                    key={balance.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.9)',
                      padding: '25px',
                      borderRadius: '12px',
                      borderLeft: `4px solid ${balance.leave_type_color}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <h4 style={{
                      color: '#2c3e50',
                      margin: '0 0 15px 0',
                      fontSize: '18px'
                    }}>
                      {balance.leave_type_name}
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '15px',
                      marginBottom: '15px'
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#27ae60'
                        }}>
                          {balance.remaining_days || 0}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#7f8c8d'
                        }}>
                          {t('leaves.days_remaining')}
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#e74c3c'
                        }}>
                          {balance.used_days || 0}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#7f8c8d'
                        }}>
                          {t('leaves.days_used')}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '14px',
                      color: '#7f8c8d',
                      borderTop: '1px solid rgba(189, 195, 199, 0.3)',
                      paddingTop: '15px'
                    }}>
                      <span>{t('leaves.total_allocated')}: {balance.total_allocated || 0}</span>
                      <span>{t('leaves.pending_days')}: {balance.pending_days || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'calendar' && (
          <LeaveCalendar user={null} />
        )}

        {activeTab === 'assigned-to-me' && (
          <div style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '20px',
              minWidth: 0
            }}>
              <h3 style={{
                color: '#2c3e50',
                margin: 0,
                fontSize: isMobile ? '1.25rem' : '24px',
                fontWeight: 'bold',
                flex: '1 1 auto',
                minWidth: 0
              }}>
                📥 {t('leaves.requests_to_validate')}
              </h3>
              {pendingAssignedCount > 0 && (
                <span style={{
                  background: '#e74c3c',
                  color: '#ecf0f1',
                  padding: '6px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}>
                  {t('leaves.pending_count', { count: pendingAssignedCount })}
                </span>
              )}
            </div>
            
            {assignedToMe.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: '#7f8c8d',
                padding: '40px'
              }}>
                <p>📭 {t('leaves.no_assigned_requests')}</p>
                <small>{t('leaves.assigned_requests_info')}</small>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                width: '100%',
                minWidth: 0
              }}>
                {assignedToMe.map(request => (
                  <div
                    key={request.id}
                    style={{
                      background: request.status === 'pending' 
                        ? 'rgba(241, 196, 15, 0.1)' 
                        : 'rgba(255, 255, 255, 0.9)',
                      padding: isMobile ? '14px' : '20px',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${getStatusColor(request.status)}`,
                      border: request.status === 'pending' ? '2px solid #f1c40f' : '1px solid #bdc3c7',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '15px',
                      gap: '10px',
                      minWidth: 0
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h4 style={{
                          color: '#2c3e50',
                          margin: '0 0 5px 0'
                        }}>
                          {request.employee_name} - {request.leave_type_name}
                        </h4>
                        <p style={{
                          color: '#7f8c8d',
                          margin: '0 0 5px 0',
                          fontSize: '14px'
                        }}>
                          {t('leaves.from')} {formatDate(request.start_date)} {t('leaves.to')} {formatDate(request.end_date)} ({request.days_requested} {t('leaves.days')})
                        </p>
                        <p style={{
                          color: '#7f8c8d',
                          margin: 0,
                          fontSize: '12px'
                        }}>
                          {t('leaves.my_level')}: {request.my_validation_step === 1 ? '👨‍💼' : request.my_validation_step === 2 ? '🏢' : '⭐'} {t('leaves.level')} {request.my_validation_step}
                        </p>
                      </div>
                      
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: getStatusColor(request.status),
                          color: '#ecf0f1',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}>
                          {getStatusText(request.status)}
                        </span>
                      </div>
                    </div>

                    {renderWorkflowProgress(request)}
                    
                    {request.reason && (
                      <div style={{
                        background: 'rgba(149, 165, 166, 0.1)',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '15px',
                        border: '1px solid #bdc3c7'
                      }}>
                        <small style={{ color: '#7f8c8d', fontWeight: '500' }}>{t('leaves.reason')}:</small>
                        <p style={{ color: '#2c3e50', margin: '5px 0 0 0', fontSize: '14px' }}>
                          {request.reason}
                        </p>
                      </div>
                    )}

                    {request.status === 'pending' && (() => {
                      const step = Number(request.current_step);
                      const myStep = Number(request.my_validation_step);
                      const isMyTurn = !request.requires_multi_validation || (request.my_validation_step != null && request.current_step != null && step === myStep);
                      if (!isMyTurn) {
                        const previousApproved = step > 1;
                        const msg = previousApproved
                          ? (t('leaves.approved_by_wait_turn') || 'Approved by others above. Your turn will come in order.')
                          : (t('leaves.waiting_previous_validators') || 'Waiting for previous validators. Your turn will come in order.');
                        return (
                          <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(149, 165, 166, 0.15)', borderRadius: '6px', fontSize: '13px', color: '#7f8c8d' }}>
                            {msg}
                          </div>
                        );
                      }
                      return (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginTop: '15px'
                      }}>
                        <button
                          onClick={() => handleValidation(request.id, 'approved')}
                          style={{
                            padding: isMobile ? '10px 14px' : '8px 16px',
                            flex: isMobile ? '1 1 120px' : undefined,
                            minWidth: isMobile ? 0 : undefined,
                            background: '#2ecc71',
                            color: '#ecf0f1',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          ✅ {t('leaves.approve')}
                        </button>
                        
                        <button
                          onClick={() => {
                            const comments = prompt(t('leaves.reject_reason_prompt'));
                            if (comments !== null) {
                              handleValidation(request.id, 'rejected', comments);
                            }
                          }}
                          style={{
                            padding: isMobile ? '10px 14px' : '8px 16px',
                            flex: isMobile ? '1 1 120px' : undefined,
                            minWidth: isMobile ? 0 : undefined,
                            background: '#e74c3c',
                            color: '#ecf0f1',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          ❌ {t('leaves.reject')}
                        </button>
                      </div>
                      );
                    })()}

                    {request.status === 'rejected' && request.rejection_reason && (
                      <div style={{
                        background: 'rgba(231, 76, 60, 0.1)',
                        border: '1px solid #e74c3c',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '10px'
                      }}>
                        <small style={{ color: '#c0392b', fontWeight: '500' }}>{t('leaves.rejection_reason_label')}</small>
                        <p style={{ color: '#2c3e50', margin: '5px 0 0 0', fontSize: '14px' }}>
                          {request.rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .leaves-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .leaves-content {
            padding: 180px 60px 60px !important;
          }
        }
        .leaves-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LeaveManagement;